import React, { useEffect, useState, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../contexts/LangContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Plus, Trash2, Clock, Building2 } from 'lucide-react';
import { ShiftTemplateTimeline } from '../components/ShiftTemplateTimeline';

// Weekday helpers (0=Mon … 6=Sun, matching Python's weekday())
const WEEKDAYS = [
  { id: 0, short: 'L', label: 'Lunes' },
  { id: 1, short: 'M', label: 'Martes' },
  { id: 2, short: 'X', label: 'Miércoles' },
  { id: 3, short: 'J', label: 'Jueves' },
  { id: 4, short: 'V', label: 'Viernes' },
  { id: 5, short: 'S', label: 'Sábado' },
  { id: 6, short: 'D', label: 'Domingo' },
];

const DEFAULT_BAND = (name = 'Mañana') => ({
  _key: `band-${Math.random().toString(36).slice(2, 9)}`,
  name,
  start_hour: 6,
  start_minute: 0,
  end_hour: 14,
  end_minute: 0,
  weekdays: [0, 1, 2, 3, 4, 5, 6],
  puesto_keywords: [],
});

const formatBand = (b) => {
  const pad = (n) => String(n ?? 0).padStart(2, '0');
  const days = b.weekdays && b.weekdays.length < 7
    ? ` (${b.weekdays.map(w => WEEKDAYS[w].short).join('')})`
    : '';
  return `${pad(b.start_hour)}:${pad(b.start_minute)}→${pad(b.end_hour)}:${pad(b.end_minute)}${days}`;
};

export default function TurnosPage() {
  const { apiFetch, user } = useAuth();
  const { t } = useLang();
  const [templates, setTemplates] = useState([]);
  const [allCenters, setAllCenters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null); // { centro_coste, shifts: [...], isNew }
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [resT, resC] = await Promise.all([
        apiFetch('/api/shift-templates'),
        apiFetch('/api/employees/facets?limit=1000'),
      ]);
      const dataT = await resT.json();
      const dataC = await resC.json();
      setTemplates(dataT?.data || []);
      // facets returns a flat list of employees; extract unique centros
      const empList = Array.isArray(dataC) ? dataC : (dataC?.data || []);
      const centrosUnicos = Array.from(new Set(empList.map(e => e.centro).filter(Boolean))).sort();
      setAllCenters(centrosUnicos);
    } catch (e) {
      if (e?.message === 'Sesión expirada') {
        toast.error('Tu sesión ha caducado. Vuelve a iniciar sesión.');
      } else {
        toast.error('No se pudieron cargar las plantillas');
      }
    }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // Centers without template yet (candidates for "+ Añadir centro")
  const centrosSinPlantilla = useMemo(() => {
    const set = new Set(templates.map(t => t.centro_coste));
    return allCenters.filter(c => !set.has(c));
  }, [allCenters, templates]);

  const startEdit = (tpl) => {
    setEditing({
      centro_coste: tpl.centro_coste,
      shifts: (tpl.shifts || []).map(s => ({ ...s, weekdays: s.weekdays || [0,1,2,3,4,5,6], puesto_keywords: s.puesto_keywords || [] })),
      compact_shifts: !!tpl.compact_shifts,
      timezone: tpl.timezone || null,
      next_day_cutoff_hour:   tpl.next_day_cutoff_hour   ?? null,
      next_day_cutoff_minute: tpl.next_day_cutoff_minute ?? null,
      split_early_morning_exit:    !!tpl.split_early_morning_exit,
      early_morning_cutoff_hour:   tpl.early_morning_cutoff_hour   ?? 6,
      early_morning_cutoff_minute: tpl.early_morning_cutoff_minute ?? 0,
      isNew: !tpl.shifts || tpl.shifts.length === 0,
    });
  };

  const startNew = (centro_coste) => {
    setEditing({
      centro_coste,
      shifts: [DEFAULT_BAND('Mañana')],
      compact_shifts: false,
      timezone: null,
      next_day_cutoff_hour: null,
      next_day_cutoff_minute: null,
      split_early_morning_exit: false,
      early_morning_cutoff_hour: 6,
      early_morning_cutoff_minute: 0,
      isNew: true,
    });
  };

  const updateBand = (idx, patch) => {
    setEditing(prev => ({
      ...prev,
      shifts: prev.shifts.map((b, i) => i === idx ? { ...b, ...patch } : b),
    }));
  };

  const updatePuestoKeywords = (idx, rawText) => {
    const keywords = rawText.split(',').map(k => k.trim()).filter(Boolean);
    updateBand(idx, { puesto_keywords: keywords, _puesto_raw: rawText });
  };

  const toggleWeekday = (idx, day) => {
    setEditing(prev => ({
      ...prev,
      shifts: prev.shifts.map((b, i) => {
        if (i !== idx) return b;
        const has = (b.weekdays || []).includes(day);
        return { ...b, weekdays: has ? b.weekdays.filter(d => d !== day) : [...(b.weekdays || []), day].sort() };
      }),
    }));
  };

  const addBand = () => {
    setEditing(prev => ({ ...prev, shifts: [...prev.shifts, DEFAULT_BAND('Tarde')] }));
  };

  const removeBand = (idx) => {
    setEditing(prev => ({ ...prev, shifts: prev.shifts.filter((_, i) => i !== idx) }));
  };

  const save = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const res = await apiFetch(`/api/shift-templates/by-centro/${encodeURIComponent(editing.centro_coste)}`, {
        method: 'PUT',
        body: JSON.stringify({
          centro_coste: editing.centro_coste,
          compact_shifts: !!editing.compact_shifts,
          timezone: editing.timezone || null,
          next_day_cutoff_hour:   editing.next_day_cutoff_hour   ?? null,
          next_day_cutoff_minute: editing.next_day_cutoff_minute ?? null,
          split_early_morning_exit:    !!editing.split_early_morning_exit,
          early_morning_cutoff_hour:   editing.early_morning_cutoff_hour   ?? 6,
          early_morning_cutoff_minute: editing.early_morning_cutoff_minute ?? 0,
          shifts: editing.shifts.map(s => ({
            name: s.name,
            start_hour: Number(s.start_hour),
            start_minute: Number(s.start_minute) || 0,
            end_hour: Number(s.end_hour),
            end_minute: Number(s.end_minute) || 0,
            weekdays: s.weekdays && s.weekdays.length < 7 ? s.weekdays : null,
            puesto_keywords: (s.puesto_keywords && s.puesto_keywords.length) ? s.puesto_keywords : null,
          })),
        }),
      });
      if (res.ok) {
        toast.success('Plantilla guardada');
        setEditing(null);
        load();
      } else {
        let detail = `Error ${res.status}`;
        try { const err = await res.json(); if (err?.detail) detail = err.detail; }
        catch (_parseErr) { /* response body not JSON — keep generic message */ }
        toast.error(detail);
      }
    } catch (e) {
      toast.error(e?.message === 'Sesión expirada'
        ? 'Tu sesión ha caducado. Vuelve a iniciar sesión.'
        : 'Error de conexión con el servidor');
    }
    setSaving(false);
  };

  const remove = async (centro_coste) => {
    if (!window.confirm(`¿Eliminar la plantilla de "${centro_coste}"? El sistema volverá a usar la clasificación global.`)) return;
    try {
      const res = await apiFetch(`/api/shift-templates/by-centro/${encodeURIComponent(centro_coste)}`, { method: 'DELETE' });
      if (res.ok) {
        toast.success('Plantilla eliminada');
        load();
      }
    } catch {
      toast.error('Error al eliminar');
    }
  };

  const filteredTemplates = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return templates;
    return templates.filter(t => (t.centro_coste || '').toLowerCase().includes(q));
  }, [templates, search]);

  // Show empty-state guidance for users without any centros asignados
  const noAccess = user?.role !== 'admin' && (!user?.allowed_centers || user.allowed_centers.length === 0);
  if (noAccess) {
    return (
      <div data-testid="turnos-no-access" style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>
        Necesitas tener al menos un centro asignado para gestionar turnos. Pide a un administrador que te dé permisos.
      </div>
    );
  }

  return (
    <div data-testid="turnos-page" style={{ padding: '24px', color: '#f1f5f9' }}>
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontSize: '24px', fontWeight: 700, marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Clock size={22} style={{ color: '#a855f7' }} />
          {t('turnos') || 'Turnos por centro'}
        </h1>
        <p style={{ color: '#94a3b8', fontSize: '13px', maxWidth: 760 }}>
          Define franjas horarias por centro de coste. El sistema clasificará automáticamente
          cada fichaje (Mañana / Tarde / Noche o nombre personalizado) según la franja correspondiente al día de la semana.
          Las franjas aquí definidas tienen prioridad sobre la configuración global.
        </p>
      </div>

      {/* SEARCH + Add new */}
      <Card style={{ background: '#1e293b', border: '1px solid #334155', padding: '16px', marginBottom: '20px' }}>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
          <Input
            data-testid="turnos-search"
            placeholder="Buscar centro de coste…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ flex: 1, minWidth: '240px', background: '#0f172a', border: '1px solid #334155', color: '#f1f5f9' }}
          />
          <select
            data-testid="turnos-add-centro"
            value=""
            onChange={e => { if (e.target.value) startNew(e.target.value); }}
            style={{ minWidth: 280, background: '#0f172a', border: '1px solid #334155', color: '#f1f5f9', borderRadius: 8, padding: '8px 12px', fontSize: '13px' }}
          >
            <option value="">+ Añadir plantilla para un centro…</option>
            {centrosSinPlantilla.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      </Card>

      {/* TABLE */}
      <Card style={{ background: '#1e293b', border: '1px solid #334155' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #334155', background: '#0f172a' }}>
              <th style={{ padding: '12px 14px', textAlign: 'left', fontSize: '12px', color: '#94a3b8', fontWeight: 600 }}>Centro de coste</th>
              <th style={{ padding: '12px 14px', textAlign: 'left', fontSize: '12px', color: '#94a3b8', fontWeight: 600 }}>Franjas</th>
              <th style={{ padding: '12px 14px', textAlign: 'right', fontSize: '12px', color: '#94a3b8', fontWeight: 600 }}>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={3} style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>Cargando…</td></tr>
            )}
            {!loading && filteredTemplates.length === 0 && (
              <tr><td colSpan={3} style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
                {templates.length === 0
                  ? 'Aún no hay plantillas. Selecciona un centro arriba para crear la primera.'
                  : 'Ningún centro coincide con tu búsqueda.'}
              </td></tr>
            )}
            {filteredTemplates.map(tpl => (
              <tr key={tpl.centro_coste} className="table-row-hover" style={{ borderBottom: '1px solid #0f172a' }}>
                <td style={{ padding: '12px 14px', fontSize: '13px', fontWeight: 500 }}>
                  <Building2 size={14} style={{ verticalAlign: 'middle', marginRight: '6px', color: '#64748b' }} />
                  {tpl.centro_coste}
                </td>
                <td style={{ padding: '12px 14px' }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center' }}>
                    {tpl.compact_shifts && (
                      <Badge data-testid={`compact-badge-${tpl.centro_coste}`} style={{ background: 'rgba(34,197,94,0.15)', border: '1px solid rgba(34,197,94,0.45)', color: '#86efac', fontFamily: 'IBM Plex Mono', fontSize: '11px' }}>
                        Compacto (1 turno/día)
                      </Badge>
                    )}
                    {tpl.timezone === 'Atlantic/Canary' && (
                      <Badge data-testid={`tz-badge-${tpl.centro_coste}`} style={{ background: 'rgba(251,146,60,0.15)', border: '1px solid rgba(251,146,60,0.45)', color: '#fdba74', fontFamily: 'IBM Plex Mono', fontSize: '11px' }}>
                        TZ Canarias
                      </Badge>
                    )}
                    {tpl.next_day_cutoff_hour !== null && tpl.next_day_cutoff_hour !== undefined && (
                      <Badge data-testid={`cutoff-badge-${tpl.centro_coste}`} style={{ background: 'rgba(59,130,246,0.15)', border: '1px solid rgba(59,130,246,0.45)', color: '#93c5fd', fontFamily: 'IBM Plex Mono', fontSize: '11px' }}>
                        Cutoff {String(tpl.next_day_cutoff_hour).padStart(2,'0')}:{String(tpl.next_day_cutoff_minute||0).padStart(2,'0')} → día+1
                      </Badge>
                    )}
                    {tpl.split_early_morning_exit && (
                      <Badge data-testid={`early-badge-${tpl.centro_coste}`} style={{ background: 'rgba(236,72,153,0.15)', border: '1px solid rgba(236,72,153,0.45)', color: '#f9a8d4', fontFamily: 'IBM Plex Mono', fontSize: '11px' }}>
                        Madrugada &lt; {String(tpl.early_morning_cutoff_hour ?? 6).padStart(2,'0')}:{String(tpl.early_morning_cutoff_minute||0).padStart(2,'0')} → día-1
                      </Badge>
                    )}
                    {(tpl.shifts || []).map((b, i) => (
                      <Badge key={i} style={{ background: 'rgba(168,85,247,0.15)', border: '1px solid rgba(168,85,247,0.45)', color: '#c4b5fd', fontFamily: 'IBM Plex Mono', fontSize: '11px' }}>
                        {b.name} · {formatBand(b)}
                      </Badge>
                    ))}
                  </div>
                </td>
                <td style={{ padding: '12px 14px', textAlign: 'right' }}>
                  <Button size="sm" variant="outline" data-testid={`edit-${tpl.centro_coste}`} onClick={() => startEdit(tpl)} style={{ marginRight: '6px' }}>Editar</Button>
                  <Button size="sm" variant="ghost" data-testid={`del-${tpl.centro_coste}`} onClick={() => remove(tpl.centro_coste)} style={{ color: '#f87171' }}>
                    <Trash2 size={14} />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {/* EDIT MODAL */}
      {editing && (
        <div data-testid="turnos-edit-modal" style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: '20px',
        }} onClick={() => !saving && setEditing(null)}>
          <Card onClick={e => e.stopPropagation()} style={{ background: '#1e293b', border: '1px solid #334155', padding: '24px', maxWidth: '760px', width: '100%', maxHeight: '88vh', overflowY: 'auto' }}>
            <div style={{ marginBottom: '20px' }}>
              <h2 style={{ fontSize: '18px', fontWeight: 700, marginBottom: '4px', color: '#f1f5f9' }}>
                {editing.isNew ? 'Nueva plantilla' : 'Editar plantilla'}
              </h2>
              <p style={{ fontSize: '13px', color: '#94a3b8' }}>{editing.centro_coste}</p>
            </div>

            {/* MODO COMPACTO — para centros de oficina donde la gente ficha
                 varias veces al día (desayuno, comida) pero es una única jornada.
                 Cuando está activo, ignoramos los gaps intermedios y calculamos
                 first_ts → last_ts como un único turno. */}
            <Card style={{ background: '#0f172a', border: '1px solid #334155', padding: '14px', marginBottom: '16px' }}>
              <label
                htmlFor="compact-shifts-toggle"
                style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', cursor: 'pointer' }}
              >
                <input
                  id="compact-shifts-toggle"
                  data-testid="compact-shifts-toggle"
                  type="checkbox"
                  checked={!!editing.compact_shifts}
                  onChange={e => setEditing(prev => ({ ...prev, compact_shifts: e.target.checked }))}
                  style={{ marginTop: '3px', accentColor: '#a855f7', width: 18, height: 18, cursor: 'pointer' }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ color: '#f1f5f9', fontSize: '14px', fontWeight: 600, marginBottom: '4px' }}>
                    Modo compacto — 1 turno por día
                  </div>
                  <div style={{ color: '#94a3b8', fontSize: '12px', lineHeight: 1.5 }}>
                    Activa esta opción para <strong style={{ color: '#c4b5fd' }}>centros de oficina</strong> donde
                    los empleados fichan varias veces al día (entrada, salida a desayuno, comida, salida final)
                    pero es <strong style={{ color: '#c4b5fd' }}>una única jornada</strong>. Se calculará
                    <strong style={{ color: '#86efac' }}> primer fichaje → último fichaje</strong> del día,
                    ignorando los descansos intermedios.
                    <br />
                    <span style={{ color: '#64748b' }}>
                      Ej. Condesa de Venadito: 08:03 → 11:00 → 13:37 → 14:58 se cuenta como <strong>08:03 → 14:58 (6.92h)</strong>
                    </span>
                  </div>
                </div>
              </label>
            </Card>

            {/* TIMEZONE — para centros fuera de la peninsula. Solo aplica a
                 datos de teletrabajo (MyTime). Los presenciales usan siempre
                 la TZ del reloj físico. Auto-detección por keyword canaria. */}
            <Card style={{ background: '#0f172a', border: '1px solid #334155', padding: '14px', marginBottom: '16px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <Label style={{ color: '#f1f5f9', fontSize: '14px', fontWeight: 600 }}>
                  Zona horaria del centro
                </Label>
                <p style={{ color: '#94a3b8', fontSize: '12px', margin: 0, lineHeight: 1.5 }}>
                  Aplica solo a fichajes de <strong style={{ color: '#c4b5fd' }}>teletrabajo (MyTime)</strong>.
                  Si es <strong style={{ color: '#c4b5fd' }}>Canarias</strong>, elige <em>Atlantic/Canary</em>.
                  Si es <strong style={{ color: '#c4b5fd' }}>Auto</strong>, el sistema detecta por el nombre del centro/organización.
                </p>
                <select
                  data-testid="template-timezone"
                  value={editing.timezone || ''}
                  onChange={e => setEditing(prev => ({ ...prev, timezone: e.target.value || null }))}
                  style={{ background: '#1e293b', border: '1px solid #334155', color: '#f1f5f9', borderRadius: 6, padding: '8px 10px', fontSize: '13px', maxWidth: '260px' }}
                >
                  <option value="">Auto (detección por nombre)</option>
                  <option value="Europe/Madrid">Península — Europe/Madrid</option>
                  <option value="Atlantic/Canary">Canarias — Atlantic/Canary</option>
                </select>
              </div>
            </Card>

            {/* NEXT DAY CUTOFF — reasignación del turno al día siguiente.
                 Ej. UTE RM VAO: 21:45. Fichajes ≥ 21:45 se cuentan como del día siguiente. */}
            <Card style={{ background: '#0f172a', border: '1px solid #334155', padding: '14px', marginBottom: '16px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <Label style={{ color: '#f1f5f9', fontSize: '14px', fontWeight: 600 }}>
                  Reasignación al día siguiente (turno nocturno)
                </Label>
                <p style={{ color: '#94a3b8', fontSize: '12px', margin: 0, lineHeight: 1.5 }}>
                  Si activas esta opción, cualquier fichaje ≥ HH:MM configurado se cuenta como perteneciente al
                  <strong style={{ color: '#c4b5fd' }}> día siguiente</strong>, y la salida de madrugada permanece en ese día.
                  <br/>
                  <span style={{ color: '#64748b' }}>
                    Ej. UTE RM VAO: cutoff <strong>21:45</strong> — entrada 21:50 del 21/07 + salida 05:00 del 22/07 se cuentan ambos en el <strong>22/07</strong> (7h10m).
                  </span>
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '6px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      data-testid="next-day-cutoff-toggle"
                      checked={editing.next_day_cutoff_hour !== null && editing.next_day_cutoff_hour !== undefined}
                      onChange={e => setEditing(prev => ({
                        ...prev,
                        next_day_cutoff_hour:   e.target.checked ? (prev.next_day_cutoff_hour ?? 21) : null,
                        next_day_cutoff_minute: e.target.checked ? (prev.next_day_cutoff_minute ?? 45) : null,
                      }))}
                      style={{ accentColor: '#a855f7', width: 16, height: 16, cursor: 'pointer' }}
                    />
                    <span style={{ color: '#f1f5f9', fontSize: '13px' }}>Activar cutoff</span>
                  </label>
                  {editing.next_day_cutoff_hour !== null && editing.next_day_cutoff_hour !== undefined && (
                    <>
                      <span style={{ color: '#64748b', fontSize: '13px', marginLeft: '4px' }}>a partir de</span>
                      <input
                        type="number" min={0} max={23}
                        value={editing.next_day_cutoff_hour ?? ''}
                        onChange={e => setEditing(prev => ({ ...prev, next_day_cutoff_hour: parseInt(e.target.value || '0', 10) }))}
                        data-testid="next-day-cutoff-hour"
                        style={{ width: 60, background: '#1e293b', border: '1px solid #334155', color: '#f1f5f9', borderRadius: 6, padding: '6px 8px', fontSize: '13px', fontFamily: 'IBM Plex Mono' }}
                      />
                      <span style={{ color: '#64748b' }}>:</span>
                      <input
                        type="number" min={0} max={59}
                        value={editing.next_day_cutoff_minute ?? ''}
                        onChange={e => setEditing(prev => ({ ...prev, next_day_cutoff_minute: parseInt(e.target.value || '0', 10) }))}
                        data-testid="next-day-cutoff-minute"
                        style={{ width: 60, background: '#1e293b', border: '1px solid #334155', color: '#f1f5f9', borderRadius: 6, padding: '6px 8px', fontSize: '13px', fontFamily: 'IBM Plex Mono' }}
                      />
                    </>
                  )}
                </div>
              </div>
            </Card>

            {/* SPLIT EARLY MORNING EXIT — para centros donde los turnos noche
                 terminan en la madrugada (típ. STA CRUZ TENERIFE). Los fichajes
                 entre 00:00 y HH:MM se reasignan al día anterior (D-1). */}
            <Card style={{ background: '#0f172a', border: '1px solid #334155', padding: '14px', marginBottom: '16px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <Label style={{ color: '#f1f5f9', fontSize: '14px', fontWeight: 600 }}>
                  Reasignar madrugada al día anterior (turnos noche)
                </Label>
                <p style={{ color: '#94a3b8', fontSize: '12px', margin: 0, lineHeight: 1.5 }}>
                  Si activas esta opción, cualquier fichaje entre <strong style={{ color: '#c4b5fd' }}>00:00 y HH:MM</strong> se
                  reasigna incondicionalmente al <strong style={{ color: '#c4b5fd' }}>día anterior</strong> (salida del
                  turno noche del día D-1). Se recomienda para centros con turnos nocturnos que salen entre
                  las 03:00 y las 06:00.
                  <br/>
                  <span style={{ color: '#64748b' }}>
                    Ej. Sta Cruz Tenerife: cutoff <strong>06:00</strong> — salida 02:04 del 11/08 pasa a contar en el <strong>10/08</strong> y la entrada 20:17 del 11/08 queda en el <strong>11/08</strong> (2 turnos separados en vez de 1 gigante de 18h).
                  </span>
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '6px', flexWrap: 'wrap' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      data-testid="split-early-morning-toggle"
                      checked={!!editing.split_early_morning_exit}
                      onChange={e => setEditing(prev => ({
                        ...prev,
                        split_early_morning_exit: e.target.checked,
                        early_morning_cutoff_hour:   prev.early_morning_cutoff_hour   ?? 6,
                        early_morning_cutoff_minute: prev.early_morning_cutoff_minute ?? 0,
                      }))}
                      style={{ accentColor: '#ec4899', width: 16, height: 16, cursor: 'pointer' }}
                    />
                    <span style={{ color: '#f1f5f9', fontSize: '13px' }}>Activar reasignación madrugada</span>
                  </label>
                  {editing.split_early_morning_exit && (
                    <>
                      <span style={{ color: '#64748b', fontSize: '13px', marginLeft: '4px' }}>fichajes antes de</span>
                      <input
                        type="number" min={0} max={23}
                        value={editing.early_morning_cutoff_hour ?? 6}
                        onChange={e => setEditing(prev => ({ ...prev, early_morning_cutoff_hour: parseInt(e.target.value || '0', 10) }))}
                        data-testid="early-morning-cutoff-hour"
                        style={{ width: 60, background: '#1e293b', border: '1px solid #334155', color: '#f1f5f9', borderRadius: 6, padding: '6px 8px', fontSize: '13px', fontFamily: 'IBM Plex Mono' }}
                      />
                      <span style={{ color: '#64748b' }}>:</span>
                      <input
                        type="number" min={0} max={59}
                        value={editing.early_morning_cutoff_minute ?? 0}
                        onChange={e => setEditing(prev => ({ ...prev, early_morning_cutoff_minute: parseInt(e.target.value || '0', 10) }))}
                        data-testid="early-morning-cutoff-minute"
                        style={{ width: 60, background: '#1e293b', border: '1px solid #334155', color: '#f1f5f9', borderRadius: 6, padding: '6px 8px', fontSize: '13px', fontFamily: 'IBM Plex Mono' }}
                      />
                      <span style={{ color: '#64748b', fontSize: '13px' }}>→ día anterior</span>
                    </>
                  )}
                </div>
              </div>
            </Card>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {editing.shifts.map((band, idx) => (
                <Card key={idx} style={{ background: '#0f172a', border: '1px solid #334155', padding: '14px' }}>
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '10px' }}>
                    <div style={{ flex: '1 1 130px' }}>
                      <Label style={{ color: '#94a3b8', fontSize: '12px' }}>Nombre</Label>
                      <select
                        data-testid={`band-name-${idx}`}
                        value={band.name}
                        onChange={e => updateBand(idx, { name: e.target.value })}
                        style={{ width: '100%', marginTop: 4, background: '#1e293b', border: '1px solid #334155', color: '#f1f5f9', borderRadius: 6, padding: '6px 10px', fontSize: '13px' }}
                      >
                        <option value="Mañana">Mañana</option>
                        <option value="Tarde">Tarde</option>
                        <option value="Noche">Noche</option>
                        <option value="Partido">Partido</option>
                      </select>
                    </div>
                    <div style={{ flex: '0 0 90px' }}>
                      <Label style={{ color: '#94a3b8', fontSize: '12px' }}>Inicio (h)</Label>
                      <input data-testid={`band-sh-${idx}`} type="number" min={0} max={23} value={band.start_hour}
                        onChange={e => updateBand(idx, { start_hour: parseInt(e.target.value || '0', 10) })}
                        style={{ width: '100%', marginTop: 4, background: '#1e293b', border: '1px solid #334155', color: '#f1f5f9', borderRadius: 6, padding: '6px 10px', fontSize: '13px', fontFamily: 'IBM Plex Mono' }} />
                    </div>
                    <div style={{ flex: '0 0 80px' }}>
                      <Label style={{ color: '#94a3b8', fontSize: '12px' }}>min</Label>
                      <input data-testid={`band-sm-${idx}`} type="number" min={0} max={59} value={band.start_minute}
                        onChange={e => updateBand(idx, { start_minute: parseInt(e.target.value || '0', 10) })}
                        style={{ width: '100%', marginTop: 4, background: '#1e293b', border: '1px solid #334155', color: '#f1f5f9', borderRadius: 6, padding: '6px 10px', fontSize: '13px', fontFamily: 'IBM Plex Mono' }} />
                    </div>
                    <div style={{ flex: '0 0 90px' }}>
                      <Label style={{ color: '#94a3b8', fontSize: '12px' }}>Fin (h)</Label>
                      <input data-testid={`band-eh-${idx}`} type="number" min={0} max={23} value={band.end_hour}
                        onChange={e => updateBand(idx, { end_hour: parseInt(e.target.value || '0', 10) })}
                        style={{ width: '100%', marginTop: 4, background: '#1e293b', border: '1px solid #334155', color: '#f1f5f9', borderRadius: 6, padding: '6px 10px', fontSize: '13px', fontFamily: 'IBM Plex Mono' }} />
                    </div>
                    <div style={{ flex: '0 0 80px' }}>
                      <Label style={{ color: '#94a3b8', fontSize: '12px' }}>min</Label>
                      <input data-testid={`band-em-${idx}`} type="number" min={0} max={59} value={band.end_minute}
                        onChange={e => updateBand(idx, { end_minute: parseInt(e.target.value || '0', 10) })}
                        style={{ width: '100%', marginTop: 4, background: '#1e293b', border: '1px solid #334155', color: '#f1f5f9', borderRadius: 6, padding: '6px 10px', fontSize: '13px', fontFamily: 'IBM Plex Mono' }} />
                    </div>
                    <Button size="sm" variant="ghost" onClick={() => removeBand(idx)} data-testid={`band-del-${idx}`} style={{ color: '#f87171' }}>
                      <Trash2 size={14} />
                    </Button>
                  </div>
                  <div>
                    <Label style={{ color: '#94a3b8', fontSize: '12px' }}>Días de la semana</Label>
                    <div style={{ display: 'flex', gap: '6px', marginTop: 6 }}>
                      {WEEKDAYS.map(d => {
                        const active = (band.weekdays || []).includes(d.id);
                        return (
                          <button
                            key={d.id}
                            type="button"
                            data-testid={`band-dow-${idx}-${d.id}`}
                            onClick={() => toggleWeekday(idx, d.id)}
                            title={d.label}
                            style={{
                              width: 30, height: 30, borderRadius: 6,
                              background: active ? 'rgba(168,85,247,0.25)' : '#1e293b',
                              border: `1px solid ${active ? 'rgba(168,85,247,0.6)' : '#334155'}`,
                              color: active ? '#c4b5fd' : '#94a3b8',
                              fontSize: 12, fontWeight: 700, cursor: 'pointer',
                            }}
                          >{d.short}</button>
                        );
                      })}
                    </div>
                  </div>
                  <div style={{ marginTop: 10 }}>
                    <Label style={{ color: '#94a3b8', fontSize: '12px' }}>
                      Restringir a puesto (opcional)
                    </Label>
                    <input
                      data-testid={`band-puesto-${idx}`}
                      type="text"
                      placeholder='Ej: "repaso rsu, limpieza rastro" — vacío = aplica a todos los puestos'
                      value={band._puesto_raw ?? (band.puesto_keywords || []).join(', ')}
                      onChange={e => updatePuestoKeywords(idx, e.target.value)}
                      style={{ width: '100%', marginTop: 4, background: '#1e293b', border: `1px solid ${(band.puesto_keywords || []).length ? 'rgba(251,146,60,0.5)' : '#334155'}`, color: '#f1f5f9', borderRadius: 6, padding: '6px 10px', fontSize: '13px' }}
                    />
                    {(band.puesto_keywords || []).length > 0 && (
                      <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
                        <Badge data-testid={`band-puesto-badge-${idx}`} style={{ background: 'rgba(251,146,60,0.15)', border: '1px solid rgba(251,146,60,0.5)', color: '#fb923c', fontSize: '11px' }}>
                          Solo para: {(band.puesto_keywords || []).join(', ')}
                        </Badge>
                      </div>
                    )}
                  </div>
                </Card>
              ))}
              <Button variant="outline" onClick={addBand} data-testid="band-add" style={{ alignSelf: 'flex-start' }}>
                <Plus size={14} style={{ marginRight: 4 }} />Añadir franja
              </Button>

              {/* Timeline visual con detección de solapes/huecos (Phase 14) */}
              <ShiftTemplateTimeline shifts={editing.shifts} />
            </div>

            <div style={{ display: 'flex', gap: 10, marginTop: 24, justifyContent: 'flex-end' }}>
              <Button variant="ghost" onClick={() => setEditing(null)} disabled={saving} data-testid="turnos-cancel">Cancelar</Button>
              <Button onClick={save} disabled={saving} data-testid="turnos-save">{saving ? 'Guardando…' : 'Guardar plantilla'}</Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
